import DiffMatchPatch from 'diff-match-patch'
import { DIFF_FIELD_CONFIG, EXCLUDED_FIELDS, type FieldConfig } from './diffConfig'

const dmp = new DiffMatchPatch()

/**
 * Represents a single diff segment
 */
export interface DiffSegment {
  type: 'equal' | 'insert' | 'delete'
  text: string
}

/**
 * Represents the diff result for a single field
 */
export interface FieldDiffResult {
  fieldPath: string
  config: FieldConfig
  previousValue: unknown
  currentValue: unknown
  hasChanged: boolean
  /** Only populated if showFullDiff is true */
  diffSegments?: DiffSegment[]
}

/**
 * Compute text diff between two strings
 * Returns an array of diff segments with type (equal/insert/delete) and text
 */
export function computeTextDiff(previous: string, current: string): DiffSegment[] {
  const diffs = dmp.diff_main(previous || '', current || '')
  dmp.diff_cleanupSemantic(diffs)

  return diffs.map(([operation, text]) => ({
    type: operation === 0 ? 'equal' : operation === 1 ? 'insert' : 'delete',
    text,
  }))
}

/**
 * Get a nested value from an object using dot notation path
 */
export function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  const keys = path.split('.')
  let current: unknown = obj

  for (const key of keys) {
    if (current === null || current === undefined) {
      return undefined
    }
    if (typeof current === 'object') {
      current = (current as Record<string, unknown>)[key]
    } else {
      return undefined
    }
  }

  return current
}

/**
 * Set a nested value in an object using dot notation path
 */
export function setNestedValue(
  obj: Record<string, unknown>,
  path: string,
  value: unknown
): void {
  const keys = path.split('.')
  let current = obj

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i]
    if (!(key in current) || typeof current[key] !== 'object') {
      current[key] = {}
    }
    current = current[key] as Record<string, unknown>
  }

  current[keys[keys.length - 1]] = value
}

/**
 * Compare two values for equality (deep comparison for objects/arrays)
 */
export function valuesAreEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a === null || b === null) return a === b
  if (a === undefined || b === undefined) return a === b
  if (typeof a !== typeof b) return false

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    return a.every((item, index) => valuesAreEqual(item, b[index]))
  }

  if (typeof a === 'object' && typeof b === 'object') {
    const aObj = a as Record<string, unknown>
    const bObj = b as Record<string, unknown>
    const aKeys = Object.keys(aObj)
    const bKeys = Object.keys(bObj)

    if (aKeys.length !== bKeys.length) return false
    return aKeys.every((key) => valuesAreEqual(aObj[key], bObj[key]))
  }

  return false
}

/**
 * Whether a value is a non-null object or array.
 *
 * Such values are rendered as a side-by-side JSON diff (with inline
 * highlighting) instead of the simple truncated before/after comparison,
 * so changes inside objects/arrays are actually visible.
 */
export function isComplexValue(value: unknown): boolean {
  return typeof value === 'object' && value !== null
}

/**
 * Format a value for display in the diff view
 */
export function formatValueForDisplay(value: unknown): string {
  if (value === null || value === undefined) {
    return '(empty)'
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }

  if (typeof value === 'string') {
    return value || '(empty)'
  }

  if (typeof value === 'number') {
    return String(value)
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return '(empty)'
    // Arrays of objects (e.g. config.tools) would stringify to "[object Object]"
    // when joined, so render those as pretty JSON instead.
    const hasObjects = value.some(
      (item) => typeof item === 'object' && item !== null
    )
    if (hasObjects) return JSON.stringify(value, null, 2)
    return value.join(', ')
  }

  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2)
  }

  return String(value)
}

/**
 * Get all field paths that exist in an object (including nested)
 * Only returns paths that are in the DIFF_FIELD_CONFIG
 */
export function getConfiguredFieldPaths(): string[] {
  return Object.keys(DIFF_FIELD_CONFIG)
}

/**
 * Compare two revision data objects and return diff results for each changed field
 */
export function computeRevisionDiff(
  previousData: Record<string, unknown>,
  currentData: Record<string, unknown>
): FieldDiffResult[] {
  const results: FieldDiffResult[] = []
  const fieldPaths = getConfiguredFieldPaths()

  for (const fieldPath of fieldPaths) {
    // Skip excluded fields
    if (EXCLUDED_FIELDS.includes(fieldPath.split('.')[0])) {
      continue
    }

    const config = DIFF_FIELD_CONFIG[fieldPath]
    const previousValue = getNestedValue(previousData, fieldPath)
    const currentValue = getNestedValue(currentData, fieldPath)

    const hasChanged = !valuesAreEqual(previousValue, currentValue)

    if (!hasChanged) {
      continue // Only include changed fields
    }

    const result: FieldDiffResult = {
      fieldPath,
      config,
      previousValue,
      currentValue,
      hasChanged,
    }

    // Compute text diff for fields that should show full diff
    if (config.showFullDiff) {
      const prevStr = formatValueForDisplay(previousValue)
      const currStr = formatValueForDisplay(currentValue)
      result.diffSegments = computeTextDiff(prevStr, currStr)
    }

    results.push(result)
  }

  return results
}

/**
 * Group diff results by category
 */
export function groupDiffsByCategory(
  diffs: FieldDiffResult[]
): Record<FieldConfig['category'], FieldDiffResult[]> {
  const groups: Record<FieldConfig['category'], FieldDiffResult[]> = {
    about: [],
    personality: [],
    instructions: [],
    telephony: [],
    integrations: [],
    notifications: [],
    admin: [],
  }

  for (const diff of diffs) {
    groups[diff.config.category].push(diff)
  }

  return groups
}

/**
 * Count total changed fields
 */
export function countChangedFields(
  previousData: Record<string, unknown>,
  currentData: Record<string, unknown>
): number {
  const diffs = computeRevisionDiff(previousData, currentData)
  return diffs.length
}

/**
 * Get a summary of changes for display
 */
export function getChangeSummary(
  previousData: Record<string, unknown>,
  currentData: Record<string, unknown>
): string {
  const diffs = computeRevisionDiff(previousData, currentData)

  if (diffs.length === 0) {
    return 'No changes'
  }

  if (diffs.length === 1) {
    return `Changed: ${diffs[0].config.label}`
  }

  if (diffs.length <= 3) {
    return `Changed: ${diffs.map((d) => d.config.label).join(', ')}`
  }

  return `Changed ${diffs.length} fields`
}
